import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import Navbar from '../components/Navbar';
import BankLogo from '../components/BankLogo';
import { ArrowRight, TrendingUp, Shield, ChevronRight, Award, Star, GitCompare, Calculator, Smartphone } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import FinanceNewsCard from '../components/FinanceNewsCard';
import AdSlot from '../components/AdSlot';
import CurrencyConverter from '../components/CurrencyConverter';
import WorldNewsCard from '../components/WorldNewsCard';
import VisitorCounter from '../components/VisitorCounter';
import TaxUrgencyPopup from '../components/TaxUrgencyPopup';
import LoanOffersBanner from '../components/LoanOffersBanner';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, login } = useContext(AuthContext);
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [testimonials, setTestimonials] = useState([]);
  const [authData, setAuthData] = useState({
    email: '', password: '', full_name: '', phone: '', referral_code: ''
  });

  // Check for referral code or ?auth=login in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    const authParam = params.get('auth');
    if (refCode) {
      setAuthData(prev => ({ ...prev, referral_code: refCode.toUpperCase() }));
      setIsLogin(false);
      setShowAuth(true);
      toast.success(`Referral code ${refCode} applied!`);
    } else if (authParam === 'login') {
      setIsLogin(true);
      setShowAuth(true);
    } else if (authParam === 'register') {
      setIsLogin(false);
      setShowAuth(true);
    }
  }, []);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const res = await axios.get(`${API}/testimonials`);
      setTestimonials(res.data);
    } catch (e) {}
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: authData.email, password: authData.password }
        : authData;
      const response = await axios.post(`${API}${endpoint}`, payload);
      login(response.data.token, response.data.user);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      setShowAuth(false);
      const params = new URLSearchParams(window.location.search);
      const redirect = params.get('redirect');
      if (redirect) {
        navigate(decodeURIComponent(redirect));
      } else if (response.data.user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/services');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    }
  };

  return (
    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <Navbar onLoginClick={() => setShowAuth(true)} />
      <TaxUrgencyPopup />

      {/* Tagline strip — fills the visible gap between navbar and hero */}
      <div data-testid="tagline-strip" style={{
        background: 'linear-gradient(90deg, #FF6B35 0%, #F97316 50%, #FB923C 100%)',
        color: 'white',
        textAlign: 'center',
        padding: '0.5rem 1rem',
        fontSize: '0.82rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.08)',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '1rem' }}>🇮🇳</span>
          India's Trusted Finance Marketplace
        </span>
        <span style={{ opacity: 0.6, fontWeight: 400 }}>·</span>
        <span style={{ opacity: 0.95 }}>Loans · Tax · Credit · Investments — All in one place</span>
      </div>

      {/* Loan offers — animated rotating banner with blinking Apply Now */}
      <LoanOffersBanner />

      {/* Hero Section */}
      <section className="hero-section fade-in">
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/603d889a-ee9b-4bf7-8ff9-d8ef5264d112/images/3ab3e6f43cd5fa6abeaf7380de70fd6694a7acda3d4915f13a011de68dede73b.png"
          alt="Modern banking"
          className="hero-bg"
        />
        <div className="hero-overlay" />
        <div className="hero-content" style={{ maxWidth: '1400px', margin: '0 auto', padding: '3.5rem 2rem 4rem', position: 'relative' }}>
          <div className="hero-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: '3rem', alignItems: 'center' }}>
          <div style={{ maxWidth: '700px' }}>
            <div style={{ display: 'inline-block', padding: '0.5rem 1rem', backgroundColor: 'rgba(255, 107, 53, 0.1)', borderRadius: '9999px', color: '#FF6B35', fontWeight: '700', fontSize: '0.875rem', marginBottom: '1rem' }}>
              ⭐ #1 Rated by 50,000+ Indian Users
            </div>
            <h1 style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', lineHeight: '1.1', marginBottom: '1.5rem', color: '#0F172A', fontWeight: '800' }}>
              Your Financial Dreams,<br /><span className="gradient-text-primary">Our Commitment</span>
            </h1>
            <p style={{ fontSize: '1.25rem', color: '#475569', marginBottom: '2rem', lineHeight: '1.8' }}>
              Access loans, banking, insurance, and investment products from India's top banks. Compare offers, calculate returns, and apply seamlessly online.
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <button 
                onClick={() => user ? navigate('/services') : setShowAuth(true)}
                className="primary-button"
                style={{ padding: '1rem 2rem', fontSize: '1.125rem' }}
                data-testid="get-started-btn"
              >
                Get Started <ArrowRight size={20} />
              </button>
              <button 
                onClick={() => navigate('/credit-score')}
                className="secondary-button"
                style={{ padding: '1rem 2rem', fontSize: '1.125rem' }}
                data-testid="check-credit-score-btn"
              >
                Free Credit Score
              </button>
              <button 
                onClick={() => navigate('/enquiry')}
                className="outline-button"
                style={{ padding: '1rem 2rem', fontSize: '1.05rem' }}
                data-testid="quick-enquiry-btn"
              >
                Quick Enquiry
              </button>
              <button 
                onClick={() => navigate('/tax-services')}
                style={{ padding: '1rem 2rem', fontSize: '1.05rem', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: 'white', border: 'none', borderRadius: '0.6rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(16,185,129,0.3)' }}
                data-testid="tax-services-cta-btn"
              >
                File ITR / Tax Services
              </button>
            </div>
            <div style={{ display: 'flex', gap: '2rem', marginTop: '2rem', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: '800', color: '#FF6B35', fontFamily: 'Outfit' }}>6+</div>
                <div style={{ color: '#475569', fontSize: '0.875rem' }}>Trusted Banks</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: '800', color: '#FF6B35', fontFamily: 'Outfit' }}>14+</div>
                <div style={{ color: '#475569', fontSize: '0.875rem' }}>Services</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: '800', color: '#FF6B35', fontFamily: 'Outfit' }}>6</div>
                <div style={{ color: '#475569', fontSize: '0.875rem' }}>Calculators</div>
              </div>
            </div>
          </div>

          {/* Right column — Live finance news */}
          <div className="hero-news-col" style={{ display: 'flex', justifyContent: 'center' }}>
            <FinanceNewsCard />
          </div>
          </div>
          <style>{`
            @media (max-width: 980px) {
              .hero-grid { grid-template-columns: 1fr !important; }
              .hero-news-col { margin-top: 1.5rem; }
            }
          `}</style>
        </div>
      </section>

      {/* Features Section */}
      <section style={{ padding: '0.75rem 2rem 0', backgroundColor: 'white' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <AdSlot slot="home_banner" fallback={false} />
        </div>
      </section>

      {/* Currency Converter (xe.com style) */}
      <CurrencyConverter />

      {/* Register Your Company CTA */}
      <section style={{ padding: '3rem 2rem', background: '#0F172A' }} data-testid="company-reg-cta">
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '2rem', alignItems: 'center' }} className="company-reg-grid">
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'rgba(255,107,53,0.15)', color: '#FDBA74', borderRadius: 999, fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.1em' }}>
              🏢 START YOUR BUSINESS
            </div>
            <h2 style={{ color: 'white', fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)', fontWeight: 800, margin: '12px 0 10px', lineHeight: 1.15 }}>
              Register your <span style={{ background: 'linear-gradient(135deg, #FF6B35 0%, #FDBA74 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>Company</span> in 7 days
            </h2>
            <p style={{ color: '#CBD5E1', fontSize: '1rem', lineHeight: 1.65, margin: '0 0 1.25rem' }}>
              Pvt Ltd, LLP, OPC or Proprietorship — all handled by verified CAs. Includes DIN, DSC, PAN, TAN, MoA &amp; AoA drafting, and bank account assistance.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.25rem' }}>
              {[
                { label: 'Proprietorship', price: '₹999' },
                { label: 'LLP', price: '₹4,999' },
                { label: 'Private Limited', price: '₹6,999' },
              ].map((p) => (
                <div key={p.label} style={{ padding: '8px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, fontSize: '0.82rem', color: 'white', fontWeight: 600 }}>
                  {p.label} <span style={{ color: '#FDBA74', marginLeft: 6 }}>{p.price}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                className="primary-button"
                onClick={() => navigate('/tax-services/business-registration')}
                data-testid="company-reg-cta-btn"
              >
                Register My Company <ArrowRight size={16} />
              </button>
              <button
                onClick={() => navigate('/tax-services')}
                style={{ padding: '0.95rem 1.5rem', background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem' }}
              >
                See All Services
              </button>
            </div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, rgba(255,107,53,0.10) 0%, rgba(255,107,53,0.02) 100%)', borderRadius: 18, padding: '1.5rem', border: '1px dashed rgba(255,107,53,0.3)' }}>
            <h4 style={{ color: 'white', margin: '0 0 1rem', fontFamily: 'Outfit, sans-serif', fontSize: '1.05rem', fontWeight: 700 }}>What you get</h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                'DIN + DSC for directors',
                'MCA name approval & filing',
                'Drafting of MoA & AoA',
                'PAN, TAN & GST registration',
                'Current account opening kit',
                'Dedicated CA support over WhatsApp',
              ].map((f) => (
                <li key={f} style={{ color: '#CBD5E1', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#10B981', fontWeight: 800 }}>✓</span> {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <style>{`@media (max-width: 800px) { .company-reg-grid { grid-template-columns: 1fr !important; } }`}</style>
      </section>

      {/* Live World/Trending News */}
      <WorldNewsCard />

      {/* Features Section */}
      <section style={{ padding: '6rem 2rem', backgroundColor: 'white' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '1rem', color: '#0F172A' }}>
              Everything You Need, In One Place
            </h2>
            <p style={{ fontSize: '1.125rem', color: '#475569', maxWidth: '600px', margin: '0 auto' }}>
              From loans to investments, plan and manage your entire financial life with Arthik
            </p>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
            <div className="service-card slide-up" style={{ cursor: 'pointer' }} onClick={() => navigate('/compare')} data-testid="feature-compare">
              <GitCompare size={48} style={{ color: '#FF6B35', marginBottom: '1.5rem' }} />
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#0F172A' }}>Compare Banks</h3>
              <p style={{ color: '#475569', lineHeight: '1.7' }}>
                Side-by-side comparison of interest rates, fees, and features across top Indian banks.
              </p>
            </div>
            
            <div className="service-card slide-up" style={{ cursor: 'pointer' }} onClick={() => navigate('/calculators')} data-testid="feature-calculators">
              <Calculator size={48} style={{ color: '#004D7A', marginBottom: '1.5rem' }} />
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#0F172A' }}>6 Smart Calculators</h3>
              <p style={{ color: '#475569', lineHeight: '1.7' }}>
                EMI, FD, RD, SIP, Tax & Eligibility calculators to plan your financial journey accurately.
              </p>
            </div>
            
            <div className="service-card slide-up" style={{ cursor: 'pointer' }} onClick={() => navigate('/credit-score')} data-testid="feature-credit-score">
              <Award size={48} style={{ color: '#FFB627', marginBottom: '1.5rem' }} />
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#0F172A' }}>Free Credit Score</h3>
              <p style={{ color: '#475569', lineHeight: '1.7' }}>
                Check your credit score for free with personalized tips to improve your financial health.
              </p>
            </div>

            <div className="service-card slide-up" style={{ cursor: 'pointer' }} onClick={() => navigate('/utilities')} data-testid="feature-utilities">
              <Shield size={48} style={{ color: '#16A34A', marginBottom: '1.5rem' }} />
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#0F172A' }}>Utility Tools</h3>
              <p style={{ color: '#475569', lineHeight: '1.7' }}>
                IFSC finder, gold/silver rates, fuel prices, and bank holiday calendar.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section style={{ padding: '6rem 2rem', backgroundColor: '#F8FAFC' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '1rem', color: '#0F172A' }}>
              Complete Financial Services
            </h2>
            <p style={{ fontSize: '1.125rem', color: '#475569', maxWidth: '600px', margin: '0 auto' }}>
              Loans, banking, insurance — all under one roof
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem' }}>
            {[
              { name: 'Personal Loan', desc: 'Up to ₹25 Lakhs', tag: 'loan' },
              { name: 'Home Loan', desc: 'From 8.40% p.a.', tag: 'loan' },
              { name: 'Business Loan', desc: 'For MSMEs', tag: 'loan' },
              { name: 'Education Loan', desc: 'Up to ₹50 Lakhs', tag: 'loan' },
              { name: 'Vehicle Loan', desc: 'From 8.70% p.a.', tag: 'loan' },
              { name: 'Credit Card', desc: 'Cashback & Rewards', tag: 'banking' },
              { name: 'Fixed Deposit', desc: 'Up to 7.50% p.a.', tag: 'banking' },
              { name: 'Health Insurance', desc: 'From ₹400/mo', tag: 'insurance' },
              { name: 'Term Life', desc: 'High coverage', tag: 'insurance' },
              { name: 'Car Insurance', desc: 'Zero depreciation', tag: 'insurance' }
            ].map((service, idx) => (
              <div 
                key={idx}
                className="service-card"
                style={{ cursor: 'pointer', padding: '1.5rem' }}
                onClick={() => navigate('/services')}
                data-testid={`service-preview-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span style={{ display: 'inline-block', padding: '0.25rem 0.6rem', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase', marginBottom: '0.75rem', backgroundColor: service.tag === 'loan' ? '#D1FAE5' : service.tag === 'banking' ? '#DBEAFE' : '#FEF3C7', color: service.tag === 'loan' ? '#065F46' : service.tag === 'banking' ? '#1E3A8A' : '#92400E' }}>
                  {service.tag}
                </span>
                <h4 style={{ fontSize: '1.125rem', marginBottom: '0.5rem', color: '#0F172A', fontWeight: '600' }}>
                  {service.name}
                </h4>
                <p style={{ color: '#475569', fontSize: '0.875rem', marginBottom: '1rem' }}>
                  {service.desc}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', color: '#FF6B35', fontWeight: '600', fontSize: '0.875rem' }}>
                  Apply Now <ChevronRight size={16} />
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <button 
              onClick={() => navigate('/services')}
              className="primary-button"
              style={{ padding: '1rem 2rem' }}
              data-testid="view-all-services-btn"
            >
              View All Services <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section style={{ padding: '6rem 2rem', backgroundColor: 'white' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
              <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '1rem', color: '#0F172A' }}>
                Trusted by Thousands
              </h2>
              <p style={{ fontSize: '1.125rem', color: '#475569' }}>
                Real stories from real people who achieved their financial goals
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
              {testimonials.slice(0, 6).map((t) => (
                <div key={t.id} className="service-card" data-testid={`testimonial-${t.id}`}>
                  <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem' }}>
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} fill={i < t.rating ? '#FFB627' : 'none'} color={i < t.rating ? '#FFB627' : '#E2E8F0'} />
                    ))}
                  </div>
                  <p style={{ color: '#0F172A', lineHeight: '1.7', marginBottom: '1.5rem', fontStyle: 'italic' }}>
                    "{t.message}"
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '1rem', borderTop: '1px solid #E2E8F0' }}>
                    <div>
                      <div style={{ fontWeight: '700', color: '#0F172A' }}>{t.name}</div>
                      <div style={{ fontSize: '0.875rem', color: '#475569' }}>{t.service_used}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Trust Section */}
      <section style={{ padding: '4rem 2rem', backgroundColor: '#F8FAFC' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', textAlign: 'center' }}>
          <h3 style={{ fontSize: '1.25rem', color: '#475569', marginBottom: '2.5rem', fontWeight: '600' }}>
            Partnered with India's Most Trusted Banks
          </h3>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '2.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              'State Bank of India',
              'HDFC Bank',
              'ICICI Bank',
              'Axis Bank',
              'Punjab National Bank',
              'Kotak Mahindra Bank'
            ].map(bankName => (
              <div key={bankName} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', minWidth: '160px' }}>
                <div style={{ width: '180px', height: '80px' }}>
                  <BankLogo bankName={bankName} size={80} />
                </div>
                <span style={{ fontSize: '0.85rem', color: '#475569', fontWeight: '600', textAlign: 'center' }}>{bankName}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '2rem', marginTop: '4rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Shield size={32} style={{ color: '#FF6B35' }} />
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: '700', color: '#0F172A' }}>100% Secure</div>
                <div style={{ fontSize: '0.875rem', color: '#475569' }}>End-to-end encryption</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Award size={32} style={{ color: '#FFB627' }} />
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: '700', color: '#0F172A' }}>RBI Compliant</div>
                <div style={{ fontSize: '0.875rem', color: '#475569' }}>Following all guidelines</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <TrendingUp size={32} style={{ color: '#004D7A' }} />
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: '700', color: '#0F172A' }}>Quick Approvals</div>
                <div style={{ fontSize: '0.875rem', color: '#475569' }}>As fast as 3 days</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ padding: '6rem 2rem', background: 'linear-gradient(135deg, #FF6B35 0%, #E55100 100%)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '3rem', alignItems: 'center' }}>
          <div style={{ textAlign: 'left' }}>
            <h2 style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)', marginBottom: '1rem', color: 'white' }}>
              Ready to Start Your <br /><span style={{ color: '#FCD34D' }}>Financial Journey?</span>
            </h2>
            <p style={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.95)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              Join thousands who have transformed their finances with Arthik. Free to start, always.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
              <button 
                onClick={() => user ? navigate('/services') : setShowAuth(true)}
                style={{ background: 'white', color: '#FF6B35', borderRadius: '0.5rem', padding: '0.85rem 1.75rem', fontWeight: '700', fontSize: '1rem', border: 'none', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}
                data-testid="cta-apply-now-btn"
              >
                Apply Now
              </button>
              <button 
                onClick={() => navigate('/enquiry')}
                style={{ background: 'transparent', color: 'white', border: '2px solid rgba(255,255,255,0.6)', borderRadius: '0.5rem', padding: '0.85rem 1.5rem', fontWeight: '700', fontSize: '1rem', cursor: 'pointer' }}
                data-testid="cta-enquiry-btn"
              >
                Quick Enquiry
              </button>
            </div>
          </div>

          {/* QR Code Card */}
          <div data-testid="enquiry-qr-card" style={{ background: 'white', borderRadius: 18, padding: '1.5rem', boxShadow: '0 18px 40px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.85rem', maxWidth: 320, margin: '0 auto' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.3rem 0.75rem', background: '#FFF7ED', color: '#9A3412', borderRadius: 999, fontSize: '0.75rem', fontWeight: 700 }}>
              <Smartphone size={14} /> Scan to Enquire
            </div>
            <div style={{ padding: 10, background: '#fff', borderRadius: 10, border: '4px solid #FF6B35' }}>
              <QRCodeSVG
                value={`${typeof window !== 'undefined' ? window.location.origin : 'https://myarthik.com'}/enquiry`}
                size={170}
                level="M"
                bgColor="#FFFFFF"
                fgColor="#0F172A"
                imageSettings={{
                  src: '/favicon.svg',
                  height: 36,
                  width: 36,
                  excavate: true,
                }}
              />
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 800, color: '#0F172A', fontSize: '1rem' }}>Quick Loan Enquiry</div>
              <div style={{ fontSize: '0.78rem', color: '#475569', marginTop: 2 }}>Point your phone camera to scan</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: '3rem 2rem 2rem', backgroundColor: '#0F172A', color: 'white' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '2rem', marginBottom: '2rem' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <img src="/arthik-logo.svg" alt="Arthik" style={{ height: 40, display: 'block', filter: 'brightness(0) invert(1)' }} />
              </div>
              <p style={{ color: '#94A3B8', fontSize: '0.875rem', lineHeight: 1.7 }}>
                India's trusted finance marketplace for loans, banking, and insurance.
              </p>
              <div style={{ marginTop: '1.25rem' }} data-testid="footer-social-icons">
                <div style={{ color: '#94A3B8', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Follow us</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {[
                    { name: 'Facebook',  href: 'https://www.facebook.com/myarthik',                 color: '#1877F2', svg: 'M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.4h-1.2c-1.2 0-1.6.8-1.6 1.6V12h2.7l-.4 2.9h-2.3v7A10 10 0 0 0 22 12z' },
                    { name: 'Instagram', href: 'https://www.instagram.com/myarthik',                color: '#E4405F', svg: 'M12 2.2c3.2 0 3.6 0 4.85.07 3.25.15 4.77 1.69 4.92 4.92.06 1.25.07 1.62.07 4.81 0 3.2 0 3.56-.07 4.81-.15 3.23-1.66 4.77-4.92 4.92-1.25.06-1.62.07-4.85.07-3.2 0-3.56 0-4.81-.07-3.27-.15-4.77-1.7-4.92-4.92-.06-1.25-.07-1.62-.07-4.81 0-3.2 0-3.56.07-4.81C2.5 3.96 4.01 2.42 7.24 2.27 8.5 2.2 8.86 2.2 12 2.2zm0 1.8c-3.14 0-3.5 0-4.74.07-2.4.11-3.3 1.04-3.41 3.41C3.78 8.7 3.78 9.05 3.78 12.2c0 3.14 0 3.5.07 4.74.11 2.36 1.01 3.3 3.41 3.41 1.24.07 1.6.07 4.74.07 3.14 0 3.5 0 4.74-.07 2.4-.11 3.3-1.05 3.41-3.41.07-1.24.07-1.6.07-4.74 0-3.14 0-3.5-.07-4.74-.11-2.37-1.02-3.3-3.41-3.41C15.5 4 15.14 4 12 4zm0 3.06A4.94 4.94 0 1 1 7.06 12 4.93 4.93 0 0 1 12 7.06zm0 8.14A3.2 3.2 0 1 0 8.8 12a3.2 3.2 0 0 0 3.2 3.2zm5.13-8.36a1.15 1.15 0 1 1-1.15-1.15 1.15 1.15 0 0 1 1.15 1.15z' },
                    { name: 'X (Twitter)', href: 'https://twitter.com/myarthik',                   color: '#000000', svg: 'M18.244 2H21l-6.59 7.53L22 22h-6.83l-4.7-6.18L4.84 22H2l7.07-8.08L2 2h6.96l4.25 5.65L18.24 2zm-2.4 18h1.9L8.27 4H6.26l9.58 16z' },
                    { name: 'LinkedIn',  href: 'https://www.linkedin.com/company/myarthik',        color: '#0A66C2', svg: 'M19 3A2 2 0 0 1 21 5v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 18V9.5H5.67V18h2.67zm-1.34-10a1.55 1.55 0 1 0 0-3.09 1.55 1.55 0 0 0 0 3.09zM18 18v-4.65c0-2.49-1.34-3.65-3.13-3.65a2.7 2.7 0 0 0-2.45 1.34V9.5h-2.67V18h2.67v-4.71c0-1.25.24-2.46 1.79-2.46 1.53 0 1.55 1.43 1.55 2.54V18H18z' },
                    { name: 'YouTube',   href: 'https://www.youtube.com/@myarthik',                color: '#FF0000', svg: 'M23.5 6.2a3 3 0 0 0-2.1-2.1C19.6 3.6 12 3.6 12 3.6s-7.6 0-9.4.5a3 3 0 0 0-2.1 2.1A31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.8.5 9.4.5 9.4.5s7.6 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z' },
                    { name: 'WhatsApp',  href: 'https://wa.me/917054986771',                       color: '#25D366', svg: 'M17.5 14.4c-.3-.1-1.7-.8-2-1s-.5-.1-.7.2-.8 1-1 1.2-.4.2-.7 0c-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.5-.6c.2-.2.2-.3.3-.5s.1-.4 0-.5l-1-2.3C8.7 6.4 8.5 6.4 8.3 6.4h-.6c-.2 0-.5.1-.8.4s-1.1 1.1-1.1 2.6 1.1 3 1.3 3.2c.2.3 2.2 3.3 5.3 4.6 1.9.8 2.6.9 3.5.8.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3zM12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.3 5L2 22l5.2-1.3A10 10 0 0 0 22 12 10 10 0 0 0 12 2z' },
                    { name: 'Telegram',  href: 'https://t.me/myarthik',                            color: '#26A5E4', svg: 'M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.24 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z' },
                    { name: 'Pinterest', href: 'https://www.pinterest.com/myarthik',               color: '#E60023', svg: 'M12 0a12 12 0 0 0-4.37 23.18c-.1-.94-.19-2.4.04-3.43.21-.92 1.36-5.84 1.36-5.84s-.35-.69-.35-1.72c0-1.61.94-2.81 2.1-2.81 1 0 1.48.74 1.48 1.63 0 1-.63 2.49-.96 3.86-.27 1.16.58 2.1 1.72 2.1 2.07 0 3.66-2.18 3.66-5.32 0-2.78-2-4.72-4.85-4.72-3.31 0-5.25 2.48-5.25 5.04 0 1 .38 2.07.86 2.65a.35.35 0 0 1 .08.33l-.32 1.32c-.05.21-.17.26-.39.16-1.46-.68-2.37-2.81-2.37-4.53 0-3.68 2.68-7.07 7.72-7.07 4.05 0 7.2 2.89 7.2 6.75 0 4.03-2.54 7.27-6.06 7.27-1.19 0-2.3-.62-2.68-1.35l-.73 2.78c-.26 1.01-.97 2.28-1.45 3.05A12 12 0 1 0 12 0z' },
                  ].map((s) => (
                    <a
                      key={s.name}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.name}
                      title={s.name}
                      data-testid={`footer-social-${s.name.toLowerCase().replace(/[^a-z]/g, '')}`}
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.10)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#CBD5E1',
                        textDecoration: 'none',
                        transition: 'all .18s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = s.color;
                        e.currentTarget.style.borderColor = s.color;
                        e.currentTarget.style.color = 'white';
                        e.currentTarget.style.transform = 'translateY(-3px) scale(1.06)';
                        e.currentTarget.style.boxShadow = `0 8px 20px ${s.color}55`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.10)';
                        e.currentTarget.style.color = '#CBD5E1';
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d={s.svg} />
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <h4 style={{ fontSize: '1rem', marginBottom: '1rem', color: 'white' }}>Quick Links</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <a onClick={() => navigate('/about')} style={{ color: '#CBD5E1', fontSize: '0.875rem', cursor: 'pointer' }}>About Us</a>
                <a onClick={() => navigate('/services')} style={{ color: '#CBD5E1', fontSize: '0.875rem', cursor: 'pointer' }}>Services</a>
                <a onClick={() => navigate('/calculators')} style={{ color: '#CBD5E1', fontSize: '0.875rem', cursor: 'pointer' }}>Calculators</a>
                <a onClick={() => navigate('/compare')} style={{ color: '#CBD5E1', fontSize: '0.875rem', cursor: 'pointer' }}>Compare</a>
                <a onClick={() => navigate('/credit-score')} style={{ color: '#CBD5E1', fontSize: '0.875rem', cursor: 'pointer' }}>Credit Score</a>
              </div>
            </div>
            <div>
              <h4 style={{ fontSize: '1rem', marginBottom: '1rem', color: 'white' }}>Support</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <a onClick={() => navigate('/contact')} style={{ color: '#CBD5E1', fontSize: '0.875rem', cursor: 'pointer' }}>Contact Us</a>
                <a onClick={() => navigate('/utilities')} style={{ color: '#CBD5E1', fontSize: '0.875rem', cursor: 'pointer' }}>Utilities</a>
                <a onClick={() => navigate('/advertise')} style={{ color: '#FDBA74', fontSize: '0.875rem', cursor: 'pointer', fontWeight: 700 }} data-testid="footer-advertise-link">📢 Advertise with Us</a>
                <a onClick={() => navigate('/consultant/signup')} style={{ color: '#6EE7B7', fontSize: '0.875rem', cursor: 'pointer', fontWeight: 700 }} data-testid="footer-ca-partner-link">🤝 Join as a CA Partner</a>
                <a href="https://myastroage.com" target="_blank" rel="noopener noreferrer" style={{ color: '#A78BFA', fontSize: '0.875rem', cursor: 'pointer', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }} data-testid="footer-astrology-link">
                  🔮 Daily Horoscope ↗
                </a>
                <a href="tel:+917054986771" style={{ color: '#CBD5E1', fontSize: '0.875rem' }} data-testid="footer-phone">+91 70549 86771</a>
                <a href="mailto:support@myarthik.com" style={{ color: '#CBD5E1', fontSize: '0.875rem' }}>support@myarthik.com</a>
              </div>
            </div>
            <div>
              <h4 style={{ fontSize: '1rem', marginBottom: '1rem', color: 'white' }}>Address</h4>
              <p style={{ color: '#CBD5E1', fontSize: '0.875rem', lineHeight: 1.7 }}>
                Arthik Finance Tower<br />
                Bandra Kurla Complex<br />
                Mumbai - 400051
              </p>
            </div>
          </div>
          <div style={{ paddingTop: '2rem', borderTop: '1px solid #1E293B', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
            <VisitorCounter />
            <p style={{ color: '#94A3B8', fontSize: '0.875rem', margin: 0 }}>
              © 2026 Arthik Finance | <a href="https://myarthik.com" style={{ color: '#FF6B35', textDecoration: 'none' }}>myarthik.com</a> | India's trusted finance marketplace
            </p>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      {showAuth && (
        <div 
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' }}
          onClick={() => setShowAuth(false)}
          data-testid="auth-modal-overlay"
        >
          <div 
            style={{ backgroundColor: 'white', borderRadius: '1.5rem', padding: '2.5rem', maxWidth: '450px', width: '100%', position: 'relative', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
            data-testid="auth-modal-content"
          >
            <button
              onClick={() => setShowAuth(false)}
              style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#475569' }}
              data-testid="close-auth-modal"
            >×</button>
            
            <h2 style={{ fontSize: '2rem', marginBottom: '0.5rem', color: '#0F172A' }}>
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p style={{ color: '#475569', marginBottom: '2rem' }}>
              {isLogin ? 'Login to continue' : 'Sign up to get started'}
            </p>

            <form onSubmit={handleAuth}>
              {!isLogin && (
                <>
                  <div style={{ marginBottom: '1rem' }}>
                    <label className="label-text">Full Name</label>
                    <input type="text" className="input-field" value={authData.full_name} onChange={(e) => setAuthData({...authData, full_name: e.target.value})} required data-testid="auth-fullname-input" />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label className="label-text">Phone Number</label>
                    <input type="tel" className="input-field" value={authData.phone} onChange={(e) => setAuthData({...authData, phone: e.target.value})} required data-testid="auth-phone-input" />
                  </div>
                </>
              )}
              <div style={{ marginBottom: '1rem' }}>
                <label className="label-text">Email</label>
                <input type="email" className="input-field" value={authData.email} onChange={(e) => setAuthData({...authData, email: e.target.value})} required data-testid="auth-email-input" />
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label className="label-text">Password {!isLogin && <span style={{ color: '#475569', fontWeight: '400', fontSize: '0.75rem' }}>(min 8 chars, 1 upper, 1 lower, 1 number)</span>}</label>
                <input type="password" className="input-field" value={authData.password} onChange={(e) => setAuthData({...authData, password: e.target.value})} required data-testid="auth-password-input" />
              </div>
              {!isLogin && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label className="label-text">Referral Code <span style={{ color: '#475569', fontWeight: '400', fontSize: '0.75rem' }}>(optional)</span></label>
                  <input type="text" className="input-field" value={authData.referral_code} onChange={(e) => setAuthData({...authData, referral_code: e.target.value.toUpperCase()})} placeholder="Got a referral code?" data-testid="auth-referral-code-input" />
                </div>
              )}
              <button type="submit" className="primary-button" style={{ width: '100%', justifyContent: 'center', marginBottom: '1rem' }} data-testid="auth-submit-button">
                {isLogin ? 'Login' : 'Create Account'}
              </button>
              <div style={{ textAlign: 'center' }}>
                <button type="button" onClick={() => setIsLogin(!isLogin)} style={{ background: 'none', border: 'none', color: '#FF6B35', fontWeight: '600', cursor: 'pointer' }} data-testid="toggle-auth-mode">
                  {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Login'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
